import {
  isWidgetEntitiesResponse,
  TextFromEntityPropertyJSON,
} from '@highimpact-frontend/api-types';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useMemo, useState } from 'react';
import { useDashboardScope } from '../../../../context/DashboardHostContext';
import type { MigratedPlotFormProps } from '../../../../forms/formHandlerShared';
import {
  getPropertyPrettyName,
  getResourcePrettyName,
} from '../../../../forms/InitiWidgetFormHandler';
import {
  getEntityId,
  getEntityProperty,
  getEntityType,
  getName,
  getPossibleEntityProperties,
  isValid,
  setElementEntityScopeFromWidgetEntity,
  setEntityId,
  setEntityProperty,
  setEntityType,
  setName,
  WithProjectSite,
} from '../../../../state/dashboard-element-operations';
import {
  findWidgetEntityByTypeAndId,
  getEntityIdOptionsFromCatalog,
} from '../shared/entitySelectionFromCatalog';
import {
  PlotConfigFormDivider,
  PlotConfigFormShell,
  PlotConfigNameField,
  PlotConfigSection,
  PlotConfigSubmitButton,
} from '../shared/plotConfigFormLayout';
import { useWidgetSourceSelection } from '../shared/useWidgetSourceSelection';

function TextFromEntityPropertyForm({
  config,
  onSubmit,
  onChange,
}: MigratedPlotFormProps) {
  const scope = useDashboardScope();
  const { loading, error, data } = useWidgetSourceSelection({
    widget: 'text_from_entity_property',
    scope,
  });
  const [submitCount, setSubmitCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [validation, setValidation] = useState(isValid(config));

  const entityCatalog = useMemo(
    () => (data && isWidgetEntitiesResponse(data) ? data.entities : []),
    [data]
  );

  const handleNameChange = (name: string) => {
    const newConfig = setName(name, config);
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const resources: TextFromEntityPropertyJSON['entity_type'][] = [
    'project',
    'site',
  ];

  const handleEntityTypeChange = (entity_type: 'project' | 'site') => {
    const newConfig = setEntityType(entity_type, config as WithProjectSite);
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleEntityIdChange = (entity_id: number) => {
    let newConfig = setEntityId(entity_id, config);
    const matchedEntity = findWidgetEntityByTypeAndId(
      entityCatalog,
      entityType,
      entity_id
    );
    if (matchedEntity) {
      newConfig = setElementEntityScopeFromWidgetEntity(
        matchedEntity,
        newConfig
      );
    }
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleEntityPropertyChange = (entity_property: string) => {
    const newConfig = setEntityProperty(entity_property, config);
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setSubmitCount(submitCount + 1);
    if (validation[0]) {
      setIsSending(true);
      onSubmit(config).finally(() => setIsSending(false));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const entityType = getEntityType(config)!;
  const entityIdOptions = getEntityIdOptionsFromCatalog(
    entityCatalog,
    entityType
  );
  const selectedEntityId = getEntityId(config);
  const entityCatalogReady = !loading && !error;

  return (
    <PlotConfigFormShell onSubmit={handleSubmit}>
      <PlotConfigNameField
        value={getName(config)}
        onChange={handleNameChange}
        errors={validation[1].name ?? []}
        submitted={submitCount > 0}
        required
      />

      <PlotConfigFormDivider />

      <PlotConfigSection title="Type de ressource">
        <FormControl size="small" fullWidth>
          <InputLabel id="text-entity-type-label">Type de ressource</InputLabel>
          <Select
            labelId="text-entity-type-label"
            value={entityType}
            label="Type de ressource"
            onChange={(event) =>
              handleEntityTypeChange(
                event.target.value as TextFromEntityPropertyJSON['entity_type']
              )
            }
          >
            {resources.map((type) => (
              <MenuItem key={type} value={type}>
                {getResourcePrettyName(type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </PlotConfigSection>

      <PlotConfigFormDivider />

      <PlotConfigSection title="Identifiant de l'entité">
        <FormControl size="small" fullWidth disabled={!entityCatalogReady}>
          <InputLabel id="text-entity-id-label">
            Identifiant de l'entité
          </InputLabel>
          <Select
            labelId="text-entity-id-label"
            value={selectedEntityId ?? ''}
            label="Identifiant de l'entité"
            onChange={(event) =>
              handleEntityIdChange(Number(event.target.value))
            }
          >
            {entityIdOptions.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading && <p>Chargement des entités...</p>}
        {error && (
          <p>Impossible de charger les entités disponibles pour ce widget.</p>
        )}
      </PlotConfigSection>

      <PlotConfigFormDivider />

      <PlotConfigSection title="Propriété de l'entité">
        <FormControl size="small" fullWidth>
          <InputLabel id="text-entity-property-label">
            Propriété de l'entité
          </InputLabel>
          <Select
            labelId="text-entity-property-label"
            value={getEntityProperty(config)}
            label="Propriété de l'entité"
            onChange={(event) =>
              handleEntityPropertyChange(String(event.target.value))
            }
          >
            {getPossibleEntityProperties(entityType).map((property) => (
              <MenuItem key={property} value={property}>
                {getPropertyPrettyName(
                  entityType,
                  property as Parameters<typeof getPropertyPrettyName>[1]
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </PlotConfigSection>

      <PlotConfigSubmitButton isSending={isSending} />
    </PlotConfigFormShell>
  );
}

export default TextFromEntityPropertyForm;
